export class BigIntString {
  //------------------- Comparisons

  eq(lhs: string, rhs: string) {
    return this.normalize(lhs) === this.normalize(rhs);
  }

  ltPositive(x: string, y: string) {
    if (this.isNegative(x) || this.isNegative(y)) {
      throw new Error("Both operands must be positive: " + x + " " + y);
    }
    const maxLength = Math.max(x.length, y.length);
    const lhs = this.leftPadZeros(x, maxLength);
    const rhs = this.leftPadZeros(y, maxLength);
    return lhs < rhs; // lexicographical comparison
  }

  lt(lhs: string, rhs: string) {
    if (this.isNegative(lhs) && this.isPositive(rhs)) {
      return true;
    } else if (this.isPositive(lhs) && this.isNegative(rhs)) {
      return false;
    } else if (this.isNegative(lhs) && this.isNegative(rhs)) {
      // Example: -3 < -5
      return !this.ltPositive(this.abs(lhs), this.abs(rhs));
    } else {
      return this.ltPositive(lhs, rhs);
    }
  }

  max(lhs: string, rhs: string) {
    return this.gt(lhs, rhs) ? lhs : rhs;
  }

  // x >= y <=> !(x < y)
  ge(lhs: string, rhs: string) {
    return !this.lt(lhs, rhs);
  }

  gt(lhs: string, rhs: string) {
    if (this.eq(lhs, rhs)) return false;
    return this.ge(lhs, rhs);
  }
  //------------------- Signs

  isNegative(strint: string) {
    this.forceString(strint);
    return strint.indexOf("-") === 0;
  }

  // Actually: isNonNegative
  isPositive(strint: string) {
    return !this.isNegative(strint);
  }

  abs(strint: string) {
    if (this.isNegative(strint)) {
      return this.negate(strint);
    } else {
      return strint;
    }
  }

  sameSign(lhs: string, rhs: string) {
    return this.isPositive(lhs) === this.isPositive(rhs);
  }

  negate(strint: string) {
    if (strint === "0") {
      return "0";
    }
    if (this.isNegative(strint)) {
      return strint.slice(1);
    } else {
      return "-" + strint;
    }
  }

  //------------------- Helpers

  RE_NON_ZERO = /^(-?)0*([1-9][0-9]*)$/;
  RE_ZERO: RegExp = /^0+$/;
  normalize(strint: string) {
    if (this.RE_ZERO.test(strint)) {
      return "0";
    }
    const match = this.RE_NON_ZERO.exec(strint);
    if (!match) {
      throw new Error("Illegal strint format: " + strint);
    }
    return match[1] + match[2];
  }

  /**
   * Prefix zeros until the length of the number is `digitCount`.
   */
  leftPadZeros(strint: string, digitCount: number) {
    this.forcePositiveString(strint);
    this.forceNonNegativeNumber(digitCount);

    return this.prefixZeros(strint, digitCount - strint.length);
  }

  prefixZeros(strint: string, zeroCount: number) {
    this.forcePositiveString(strint);
    this.forceNonNegativeNumber(zeroCount);

    let result = strint;
    for (let i = 0; i < zeroCount; i++) {
      result = "0" + result;
    }
    return result;
  }

  shiftLeft(strint: string, digitCount: number) {
    while (digitCount > 0) {
      strint = strint + "0";
      digitCount--;
    }
    return strint;
  }

  /**
   * Works for negative numbers, too.
   * Index of rightmost digit is 0. Going too far left results in "0".
   */
  getDigit(x: string, digitIndex: number) {
    this.forceString(x);
    this.forceNumber(digitIndex);
    if (digitIndex >= this.getDigitCount(x)) {
      return "0";
    } else {
      return x.charAt(x.length - digitIndex - 1);
    }
  }

  getDigitCount(strint: string) {
    if (this.isNegative(strint)) {
      return strint.length - 1;
    } else {
      return strint.length;
    }
  }
  //------------------- Type checks

  forceString(value: string) {
    this.forceType(value, "string");
  }
  forcePositiveString(value: string) {
    this.forceString(value);
    this.forceCondition(value, this.isPositive.bind(this), "isPositive");
  }
  forceNumber(value: number) {
    this.forceType(value, "number");
  }
  forceNonNegativeNumber(value: number) {
    this.forceType(value, "number");
    if ((value as number) < 0) {
      throw new Error("Expected a positive number: " + value);
    }
  }
  forceCondition(value: string, condition: (v: string) => boolean, conditionName: string) {
    if (!condition.call(null, value)) {
      throw new Error("Condition " + conditionName + " failed for value " + value);
    }
  }
  forceType(value: string | number, type: "string" | "number") {
    if (typeof value !== type) {
      throw new Error("Not a " + type + ": " + value);
    }
  }

  //-------------------
}
